/**
 * Pure (React-free, handler-free) DSL-theme → flat WebsiteConfig fields.
 * Split out from persistWebsiteDraft so unit tests can import it without
 * pulling the API-handler module graph.
 */
import type { WebsiteConfig } from "@/types/WebsiteConfig";
import type { WebsiteThemeId } from "@/types/WebsiteTheme";
import type {
  BodyTheme,
  ButtonsTheme,
  FormInputTheme,
  HeadingsTheme,
  HyperlinkTheme,
} from "@/types/WBTheme";
import {
  BODY_THEME_KEYS,
  H1_THEME_KEYS,
  H2_THEME_KEYS,
  PRIMARY_BTN_KEYS,
  SECONDARY_BTN_KEYS,
  HYPERLINK_THEME_KEYS,
  FORM_INPUT_THEME_KEYS,
} from "@/types/WBTheme";
import { syncThemeFromPalette } from "@/components/WebsiteBuilder/settings/syncThemeFromPalette";
import {
  BASE_THEME_DEFAULTS,
  getThemePreset,
} from "@/components/builders/website/configurator-panel/components/themes";
import type { WebsiteThemeBlock } from "./websiteBlocksDsl";

const STRICT_HEX = /^#[0-9A-Fa-f]{6}$/;

export const VALID_PRESETS = new Set<WebsiteThemeId>([
  "webinar", "speaker", "charity", "workshop", "corporate", "dinner",
  "networking", "networkingNight", "summit", "soiree", "press", "gala", "productLaunch",
]);

export function isPreset(id: string | undefined): id is WebsiteThemeId {
  return !!id && VALID_PRESETS.has(id as WebsiteThemeId);
}

export type ThemeGroups = {
  bodyTheme: BodyTheme;
  headingsTheme: HeadingsTheme;
  buttonsTheme: ButtonsTheme;
  hyperlinkTheme: HyperlinkTheme;
  formInputTheme: FormInputTheme;
};

function appendGroup<T extends Record<string, unknown>>(
  out: Record<string, unknown>,
  group: T | undefined,
  keyMap: Record<keyof T, keyof WebsiteConfig>,
): void {
  for (const key of Object.keys(keyMap) as (keyof T)[]) {
    const value = group?.[key];
    if (value === undefined) continue;
    out[keyMap[key] as string] = value;
  }
}

/**
 * Flatten the five element-theme groups into their WebsiteConfig columns —
 * every defined leaf, no diffing.
 *
 * The lanes that write a website used to persist only what DIFFERED from
 * BASE_THEME_DEFAULTS. That is harmless for a create (the row starts empty) but
 * wrong for the template-overwrite lane, whose PUT is a `$set` merge
 * (DocumentMapper.toUpdateDocument skips nulls): a field the NEW template leaves
 * at the base value was simply absent, so the OLD template's value survived and
 * the site came out half-reskinned — square buttons under a rounded template,
 * underlined links under one that doesn't underline.
 *
 * Persisting explicit values does not freeze the later Design-tab cascade:
 * syncThemeFromPalette's `pickDeep` decides "auto vs hand-picked" by VALUE
 * (`current === derivedOld`), not by presence.
 */
export function flattenThemeGroups(groups: ThemeGroups): Partial<WebsiteConfig> {
  const out: Record<string, unknown> = {};
  appendGroup(out, groups.bodyTheme, BODY_THEME_KEYS);
  appendGroup(out, groups.headingsTheme?.h1, H1_THEME_KEYS);
  appendGroup(out, groups.headingsTheme?.h2, H2_THEME_KEYS);
  appendGroup(out, groups.buttonsTheme?.primary, PRIMARY_BTN_KEYS);
  appendGroup(out, groups.buttonsTheme?.secondary, SECONDARY_BTN_KEYS);
  appendGroup(out, groups.hyperlinkTheme, HYPERLINK_THEME_KEYS);
  appendGroup(out, groups.formInputTheme, FORM_INPUT_THEME_KEYS);
  return out as Partial<WebsiteConfig>;
}

/**
 * Build the flat WebsiteConfig theme fields from the DSL theme block.
 *
 * Palette colors fall back to the chosen preset (or base defaults) and are
 * STRICT 6-hex on create (§0b.3) — an invalid override falls back to the
 * baseline rather than 400ing.
 *
 * The per-component fields used to be omitted entirely "so the runtime syncs
 * them from the palette". Only the COLOR leaves can be derived that way; sizes,
 * radii, border styling and the preset's `customCss` cannot, so the Setup-Hub
 * "Build" lane and the Velo DSL lane shipped every template stripped of its
 * typography scale and — because each preset keeps its self-hosted @font-face,
 * hero gradient and motion in 2.2–3.4 KB of customCss — of its hero art and
 * display font too. The same template therefore looked different depending on
 * which door created it. Now the whole preset is materialised here.
 */
export function buildThemeConfig(theme: WebsiteThemeBlock | undefined): Partial<WebsiteConfig> {
  const base = isPreset(theme?.preset) ? getThemePreset(theme!.preset as WebsiteThemeId) : BASE_THEME_DEFAULTS;
  const pick = (c: string | undefined, fallback: string) => (c && STRICT_HEX.test(c) ? c : fallback);

  const palette = {
    primary: pick(theme?.primaryColor, base.colorPalette.primary),
    secondary: pick(theme?.secondaryColor, base.colorPalette.secondary),
    background: pick(theme?.backgroundColor, base.colorPalette.background),
  };
  const headerFont = theme?.headerFont || base.fonts.header;
  const bodyFont = theme?.bodyFont || base.fonts.body;
  const rounding = theme?.rounding || base.rounding;

  // Full reset (no oldPalette) — the same derivation useApplyThemePreset runs.
  // This is what keeps a DSL `primaryColor` override cascading into headings and
  // buttons instead of being overwritten by the preset's own hex.
  const synced = syncThemeFromPalette(
    {
      bodyTheme: base.bodyTheme,
      headingsTheme: base.headingsTheme,
      buttonsTheme: base.buttonsTheme,
      hyperlinkTheme: base.hyperlinkTheme,
      formInputTheme: base.formInputTheme,
    },
    palette,
  );

  // Font / rounding cascades mirror the wizard's design step — and run ONLY on
  // an explicit override. Unconditionally re-deriving them would destroy
  // deliberate pairings: `press` sets a serif body font against sans-serif form
  // inputs, and the preset's own h1 family is not always `fonts.header`.
  if (theme?.headerFont) {
    synced.headingsTheme = {
      h1: { ...synced.headingsTheme.h1, fontFamily: headerFont },
      h2: { ...synced.headingsTheme.h2, fontFamily: headerFont },
    };
  }
  if (theme?.bodyFont) {
    synced.formInputTheme = { ...synced.formInputTheme, fontFamily: bodyFont };
  }
  if (theme?.rounding) {
    synced.formInputTheme = { ...synced.formInputTheme, borderRadius: rounding };
    synced.buttonsTheme = {
      primary: { ...synced.buttonsTheme.primary, borderRadius: rounding, hoverBorderRadius: rounding },
      secondary: { ...synced.buttonsTheme.secondary, borderRadius: rounding, hoverBorderRadius: rounding },
    };
  }

  return {
    primaryColor: palette.primary,
    secondaryColor: palette.secondary,
    backgroundColor: palette.background,
    headerFont,
    bodyFont,
    rounding,
    // Always a string, never omitted: on the merge-PUT lane only a sent "" can
    // clear a previous template's stylesheet.
    customCss: theme?.customCss ?? base.customCss ?? "",
    ...flattenThemeGroups(synced),
  };
}
